import http from '@/lib/http'
import { brandLabel } from '@/lib/medication-options'
import type { Medication } from '@/types/data'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'

type ProductVariantRow = {
  hash: string
  variation_hash: string | null
  product_hash: string
  product_title: string
  product_sku: string
  product_description: string | null
  product_directions: string | null
  prod_group: string | null
  variant_title: string | null
  variant_sku: string | null
  price: string | null
  is_in_stock: boolean
  variant_directions: string | null
  strength: string | null
  quantity: string | null
  frequency: string | null
  brand: string | null
  product_pricing: string | null
  categories: Array<{ hash: string; name: string }>
}

// a row is a variant iff the product_variation table matched: `hash` is NOT NULL there and the
// write path always backfills it, whereas `sku` defaults to '' and the default variation inherits
// the parent's SKU — so SKU can neither prove nor disprove variant-ness
const isVariantRow = (p: ProductVariantRow) => !!p.variation_hash

/**
 * `is_in_stock` is read off the variation row, so a product that has no variation row at all
 * (hash falls back to the product hash) reports false by omission rather than by being sold out.
 * Only a variation-backed row carries a real stock signal; everything else is treated as
 * available so genuinely prescribable products are not blocked by a missing column.
 */
const isInStock = (p: ProductVariantRow) => p.hash === p.product_hash || p.is_in_stock

const categoryHashesOf = (p: ProductVariantRow) => (p.categories ?? []).map(category => category.hash)

const buildKey = (
  title: string | null | undefined,
  brand: string | null,
  strength: string | null,
  quantity: string | null
) => {
  const parts = [brandLabel(brand, title ?? ''), strength, quantity].filter(Boolean)
  return parts.length ? `${title} - ${parts.join(' ')}` : `${title ?? ''}`
}

export const mapProductsToMedications = (products: ProductVariantRow[]): Medication[] => {
  const result: Medication[] = []

  for (const product of products) {
    const key = buildKey(product.product_title, product.brand, product.strength, product.quantity)

    if (isVariantRow(product)) {
      result.push({
        id: product.hash ?? '',
        wooSKU: product.hash ?? '',
        variantHash: product.hash ?? null,
        productHash: product.product_hash ?? null,
        productName: key,
        type: product.prod_group?.toUpperCase() === 'POM' ? ('POM' as const) : ('OTC' as const),
        // Dose directions come from the variant and only the variant: a variant with none is a gap
        // for the prescriber to fill, not a cue to fall back to the product's.
        medicationDirections: product.variant_directions || '',
        subscriptionFrequency: product.frequency ?? null,
        strength: product.strength ?? null,
        quantity: product.quantity ?? null,
        productTitle: product.product_title ?? null,
        variantTitle: product.variant_title ?? null,
        brand: product.brand ?? null,
        isInStock: isInStock(product),
        categoryHashes: categoryHashesOf(product)
      })
    } else {
      result.push({
        id: product.product_hash ?? '',
        wooSKU: product.hash || product.product_hash || '',
        variantHash: product.hash ?? null,
        productHash: product.product_hash ?? null,
        productName: key,
        type: product.prod_group?.toUpperCase() === 'POM' ? ('POM' as const) : ('OTC' as const),
        // No variation row means no variant directions to carry; the product's are deliberately not
        // used, matching `productLevelMedicationFor`'s product-level stand-in.
        medicationDirections: '',
        subscriptionFrequency: product.frequency ?? null,
        strength: product.strength ?? null,
        quantity: product.quantity ?? null,
        productTitle: product.product_title ?? null,
        variantTitle: product.variant_title ?? null,
        brand: product.brand ?? null,
        isInStock: isInStock(product),
        categoryHashes: categoryHashesOf(product)
      })
    }
  }

  return result
}

const fetchMedications = () =>
  http.get<ProductVariantRow[], ProductVariantRow[]>('/products').then(mapProductsToMedications)

export const useGetMedications = () => {
  return useQuery<Medication[]>({
    queryKey: ['getMedications'],
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: fetchMedications
  })
}

export const useGetSuspenseMedications = () => {
  return useSuspenseQuery<Medication[]>({
    queryKey: ['getMedications'],
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: fetchMedications
  })
}
